/**
 * Classifies an app version the way git-flow (`@cpdevtools/git-flow/version`)
 * produced it, so the UI can tell a mainline release from a pre-release and
 * from a branch/re-publish build using only the version string.
 *
 * git-flow's `resolveVersion` emits exactly these shapes:
 *
 *   mainline branch (no `/`), first publish       X.Y.Z  |  X.Y.Z-<channel>.<n>
 *   mainline branch, version already tagged       X.Y.Z-<branch>.build.<run>  |  X.Y.Z-<channel>.<n>.build.<run>
 *   development branch (`feature/...`, `team/x/y`) X.Y.Z-<branch with / → .>[.<channel>.<n>][.build.<run>]
 *
 * `<channel>` is a single identifier (`alpha`, `beta`, `rc`, …) followed by a
 * counter; a sanitized development branch name is at least two identifiers
 * because `isMainlineBranch` is "no slash in the branch name".
 */
export type BranchType = 'mainline' | 'development';

export interface ParsedVersion {
  /** `X.Y.Z` */
  readonly base: string;
  /** Everything after the hyphen (`feature.wire-cut.alpha.0.build.6`), empty for a release. */
  readonly prerelease: string;
  /** Whether the version has any prerelease identifiers. */
  readonly isPreRelease: boolean;
  /** The CI run number when git-flow appended `.build.<n>` (version was already tagged). */
  readonly buildNumber?: number;
  /** git-flow branch type the version was resolved on. */
  readonly branchType: BranchType;
}

const SEMVER = /^v?(\d+\.\d+\.\d+)(?:-([0-9A-Za-z.-]+))?(?:\+[0-9A-Za-z.-]+)?$/;
const NUMERIC = /^\d+$/;
const IDENTIFIER = /^[A-Za-z][0-9A-Za-z-]*$/;

export function parseVersion(version: string): ParsedVersion | undefined {
  const match = SEMVER.exec(version.trim());
  if (!match) return undefined;
  const [, base, prerelease = ''] = match;
  const identifiers = prerelease ? prerelease.split('.') : [];

  // `.build.<n>` is only ever appended by git-flow, so the last `build` followed by a number is the run number.
  let buildNumber: number | undefined;
  let core = identifiers;
  const buildIndex = identifiers.lastIndexOf('build');
  if (buildIndex > 0 && buildIndex === identifiers.length - 2 && NUMERIC.test(identifiers[buildIndex + 1])) {
    buildNumber = Number(identifiers[buildIndex + 1]);
    core = identifiers.slice(0, buildIndex);
  }

  return {
    base,
    prerelease,
    isPreRelease: identifiers.length > 0,
    ...(buildNumber !== undefined ? { buildNumber } : {}),
    branchType: branchTypeOf(core),
  };
}

/**
 * Mirrors git-flow's `isMainlineBranch` from the version side: a mainline
 * pre-release is `<channel>.<n>…` (one word, then counters); anything with a
 * second non-numeric identifier before the counters is a sanitized branch name.
 */
function branchTypeOf(core: readonly string[]): BranchType {
  if (core.length === 0) return 'mainline';
  const [channel, ...rest] = core;
  if (IDENTIFIER.test(channel) && rest.every((part) => NUMERIC.test(part))) return 'mainline';
  return 'development';
}
